import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Badge, Button, Input } from 'reactstrap'
import axios from 'axios'
import moment from 'moment'

import { setModal } from '../../common/GlobalModal/redux/GlobalModalActions'
import { acquireCrmSocket } from '../../utils/crmSocket'
import { notificationDanger } from '../../utils/customToastify'
import { emitSmsUnreadChanged } from './smsReadSignal'
import useSendSms from './useSendSms'
import './MiniMessenger.scss'

const MAX_SMS_LENGTH = 1600

// Filter toggles persist per-browser so a decluttered view survives reopening
// the widget. Storage can throw (private mode / disabled) so both are guarded.
const ONLY_UNREAD_CONVOS_KEY = 'sms_only_unread_convos'
const ONLY_UNREAD_THREAD_KEY = 'sms_only_unread_thread'
const readFlag = (key) => {
  try {
    return localStorage.getItem(key) === '1'
  } catch (_) {
    return false
  }
}
const writeFlag = (key, value) => {
  try {
    localStorage.setItem(key, value ? '1' : '0')
  } catch (_) {
    /* storage unavailable — ignore */
  }
}

// Sidebar search scopes: find a contact to text, search all my messages, or
// search only the open conversation's messages.
const SEARCH_SCOPES = [
  { value: 'contacts', label: 'Contacts', placeholder: 'Search contacts…' },
  { value: 'all', label: 'All msgs', placeholder: 'Search all messages…' },
  { value: 'chat', label: 'This chat', placeholder: 'Search this conversation…' },
]
const SEARCH_MIN_CHARS = 2
// How long a just-arrived message (and its conversation row) stays highlighted.
const FLASH_MS = 2500

const formatPhoneNumber = (phone) => {
  if (!phone) return ''
  const cleaned = `${phone}`.replace(/\D/g, '')
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
  }
  return phone
}

const conversationTitle = (convo) => convo.contactName || formatPhoneNumber(convo.phone)

const dayLabel = (date) => {
  const day = moment(date)
  if (day.isSame(moment(), 'day')) return 'Today'
  if (day.isSame(moment().subtract(1, 'day'), 'day')) return 'Yesterday'
  return day.format('MMM DD, YYYY')
}

/**
 * Mini messenger (po uzoru na RingCentral aplikaciju): levo lista skorasnjih
 * konverzacija ulogovanog korisnika, desno chat sa bubble-ovima i compose
 * poljem. Otvara se iz Messages header widgeta; izbor konverzacije je markira
 * procitanom, a incoming_sms socket event osvezava i listu i otvoren chat.
 */
const MiniMessenger = ({ initialContactId = null, initialPhone = null }) => {
  const dispatch = useDispatch()
  const [conversations, setConversations] = useState([])
  const [selected, setSelected] = useState(null)
  const [thread, setThread] = useState([])
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [onlyUnreadConvos, setOnlyUnreadConvos] = useState(() => readFlag(ONLY_UNREAD_CONVOS_KEY))
  const [onlyUnreadThread, setOnlyUnreadThread] = useState(() => readFlag(ONLY_UNREAD_THREAD_KEY))
  // Ids of messages that were unread when the open thread was loaded. Opening a
  // conversation marks it read right away, so the live read_at can no longer tell
  // us what was "new" — this snapshot keeps the unread filter meaningful.
  const [unreadThreadIds, setUnreadThreadIds] = useState(() => new Set())
  // Ids/keys that just received a live message — drives a brief highlight while
  // the messenger is open, then clears itself.
  const [flashMessageIds, setFlashMessageIds] = useState(() => new Set())
  const [flashConvoKeys, setFlashConvoKeys] = useState(() => new Set())
  const [searchScope, setSearchScope] = useState('contacts')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const sendSms = useSendSms()
  const threadEndRef = useRef(null)
  const selectedRef = useRef(null)
  selectedRef.current = selected

  const loadConversations = async () => {
    try {
      const response = await axios.get('/ringcentral-sms/conversations')
      const data = response.data.data || []
      setConversations(data)
      return data
    } catch (error) {
      notificationDanger('Failed to load conversations')
      return []
    }
  }

  const loadThread = async (convo, { resetUnread = false } = {}) => {
    try {
      const response = await axios.get('/ringcentral-sms/thread', {
        params: convo.contactId ? { contactId: convo.contactId } : { phone: convo.phone },
      })
      const data = response.data.data || []
      setThread(data)
      // Snapshot which messages are unread. resetUnread starts fresh (new
      // conversation opened); otherwise union so a live incoming message adds to
      // the set the "unread only" view is showing.
      setUnreadThreadIds((prev) => {
        const next = resetUnread ? new Set() : new Set(prev)
        data.forEach((sms) => {
          if (sms.direction === 'Inbound' && !sms.readAt) next.add(sms.id)
        })
        return next
      })
    } catch (error) {
      setThread([])
    }
  }

  const markRead = async (convo) => {
    try {
      await axios.post('/ringcentral-sms/mark-read', {
        contactId: convo.contactId || null,
        fromNumber: convo.contactId ? null : convo.phone,
      })
      setConversations((prev) =>
        prev.map((item) => (item.convoKey === convo.convoKey ? { ...item, unreadCount: 0 } : item))
      )
      // Odmah osvezi header badge (odvojena komponenta) bez rucnog refresha
      emitSmsUnreadChanged()
    } catch (error) {
      // nije kriticno — badge ostaje do sledeceg refresha
    }
  }

  const selectConversation = async (convo) => {
    setSelected(convo)
    // Load (and snapshot unread) before marking read, otherwise the mark-read
    // write can win the race and the unread filter would show nothing.
    await loadThread(convo, { resetUnread: true })
    if (convo.unreadCount > 0) markRead(convo)
  }

  const toggleOnlyUnreadConvos = () => {
    setOnlyUnreadConvos((prev) => {
      writeFlag(ONLY_UNREAD_CONVOS_KEY, !prev)
      return !prev
    })
  }

  const toggleOnlyUnreadThread = () => {
    setOnlyUnreadThread((prev) => {
      writeFlag(ONLY_UNREAD_THREAD_KEY, !prev)
      return !prev
    })
  }

  const clearSearch = () => {
    setSearchQuery('')
    setSearchResults([])
  }

  // Open the conversation for a contact picked from contact-search (starts a new
  // one if there is no history yet). mobile → business → home for the send-to number.
  const openContactResult = (contactRow) => {
    const phone = contactRow.mobilePhone || contactRow.businessPhone || contactRow.homePhone || ''
    clearSearch()
    selectConversation({
      contactId: contactRow.id,
      contactName: contactRow.name,
      phone: `${phone}`.replace(/\D/g, ''),
      convoKey: `c:${contactRow.id}`,
      unreadCount: 0,
    })
  }

  // Jump to the conversation a matched message belongs to.
  const openMessageResult = (message) => {
    clearSearch()
    selectConversation({
      contactId: message.contactId || null,
      contactName: message.contactName,
      phone: message.contactId ? '' : message.otherNumber,
      convoKey: message.contactId ? `c:${message.contactId}` : `p:${message.otherNumber}`,
      unreadCount: 0,
    })
  }

  useEffect(() => {
    const init = async () => {
      const data = await loadConversations()
      let target = null
      if (initialContactId) target = data.find((c) => c.contactId === initialContactId)
      if (!target && initialPhone) target = data.find((c) => !c.contactId && c.phone === initialPhone)
      if (!target) target = data[0]
      if (target) selectConversation(target)
    }
    init()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const flashItem = (setter, key) => {
      setter((prev) => new Set(prev).add(key))
      setTimeout(() => {
        setter((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }, FLASH_MS)
    }
    const onIncomingSms = (payload = {}) => {
      loadConversations()
      const current = selectedRef.current
      if (current) {
        loadThread(current)
        if (current.unreadCount === 0) markRead(current)
      }
      // Briefly highlight the just-arrived message and its conversation row.
      if (payload.smsId) flashItem(setFlashMessageIds, payload.smsId)
      const convoKey = payload.contactId
        ? `c:${payload.contactId}`
        : payload.fromNumber
        ? `p:${payload.fromNumber}`
        : null
      if (convoKey) flashItem(setFlashConvoKeys, convoKey)
    }
    const connection = acquireCrmSocket({ incoming_sms: onIncomingSms })
    if (!connection) return
    return () => connection.release()
    // eslint-disable-next-line
  }, [])

  // Debounced search. Contacts scope hits contact-search; message scopes hit
  // message-search (both user_id-scoped server-side). "This chat" narrows to the
  // open conversation by contactId (or phone for unlinked threads).
  useEffect(() => {
    const q = searchQuery.trim()
    if (q.length < SEARCH_MIN_CHARS) {
      setSearchResults([])
      setSearchLoading(false)
      return
    }
    setSearchLoading(true)
    const handle = setTimeout(async () => {
      try {
        if (searchScope === 'contacts') {
          const res = await axios.get('/ringcentral-sms/contact-search', { params: { q } })
          setSearchResults((res.data.data || []).map((row) => ({ kind: 'contact', ...row })))
        } else {
          const params = { q }
          const current = selectedRef.current
          if (searchScope === 'chat' && current) {
            if (current.contactId) params.contactId = current.contactId
            else if (current.phone) params.phone = current.phone
          }
          const res = await axios.get('/ringcentral-sms/message-search', { params })
          setSearchResults((res.data.data || []).map((row) => ({ kind: 'message', ...row })))
        }
      } catch (error) {
        setSearchResults([])
      } finally {
        setSearchLoading(false)
      }
    }, 300)
    return () => clearTimeout(handle)
  }, [searchQuery, searchScope, selected])

  useEffect(() => {
    if (threadEndRef.current) threadEndRef.current.scrollIntoView({ block: 'end' })
  }, [thread])

  const send = async () => {
    if (!selected || !text.trim()) return
    try {
      setSending(true)
      // P2P send from the user's own RC number (native RC Text style)
      const result = await sendSms({
        contactId: selected.contactId || null,
        toNumber: selected.phone,
        text: text.trim(),
      })
      setText('')
      if (result && result.data) {
        setThread((prev) => [...prev, result.data])
      }
      loadConversations()
    } catch (error) {
      notificationDanger(error.response?.data?.message || error.message || 'Failed to send SMS')
    } finally {
      setSending(false)
    }
  }

  const onComposeKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      send()
    }
  }

  const visibleConversations = onlyUnreadConvos ? conversations.filter((convo) => convo.unreadCount > 0) : conversations
  const visibleThread = onlyUnreadThread ? thread.filter((sms) => unreadThreadIds.has(sms.id)) : thread
  const searchActive = searchQuery.trim().length >= SEARCH_MIN_CHARS
  const searchPlaceholder = (SEARCH_SCOPES.find((s) => s.value === searchScope) || SEARCH_SCOPES[0]).placeholder

  let lastDay = null

  return (
    <div className='mini-messenger'>
      <div className='mini-messenger__sidebar'>
        <div className='mini-messenger__sidebar-header'>
          <strong>Text</strong>
          <div className='mini-messenger__header-actions'>
            <i
              className={`fas fa-envelope-open-text mini-messenger__filter ${
                onlyUnreadConvos ? 'mini-messenger__filter--active' : ''
              }`}
              title={
                onlyUnreadConvos ? 'Showing only contacts with new messages' : 'Show only contacts with new messages'
              }
              onClick={toggleOnlyUnreadConvos}
            />
            <i
              className='fas fa-pen'
              title='New Message'
              onClick={() => dispatch(setModal({ id: 'sms_new_message_modal', name: 'New Message' }))}
            />
          </div>
        </div>
        <div className='mini-messenger__search'>
          <Input
            bsSize='sm'
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className='mini-messenger__search-scopes'>
            {SEARCH_SCOPES.map((scope) => (
              <button
                key={scope.value}
                type='button'
                className={`mini-messenger__scope ${
                  searchScope === scope.value ? 'mini-messenger__scope--active' : ''
                }`}
                disabled={scope.value === 'chat' && !selected}
                onClick={() => setSearchScope(scope.value)}
              >
                {scope.label}
              </button>
            ))}
          </div>
        </div>
        <div className='mini-messenger__list'>
          {searchActive ? (
            <>
              {searchLoading && <p className='text-muted text-center mt-3'>Searching…</p>}
              {!searchLoading && searchResults.length === 0 && (
                <p className='text-muted text-center mt-3'>No matches</p>
              )}
              {!searchLoading &&
                searchResults.map((row) =>
                  row.kind === 'contact' ? (
                    <div key={`c-${row.id}`} className='mini-messenger__convo' onClick={() => openContactResult(row)}>
                      <strong className='mini-messenger__convo-name'>{row.name}</strong>
                      <div>
                        <small className='mini-messenger__convo-preview'>
                          {formatPhoneNumber(row.mobilePhone || row.businessPhone || row.homePhone)}
                        </small>
                      </div>
                    </div>
                  ) : (
                    <div key={`m-${row.id}`} className='mini-messenger__convo' onClick={() => openMessageResult(row)}>
                      <div className='d-flex justify-content-between align-items-center'>
                        <strong className='mini-messenger__convo-name'>
                          {row.contactName || formatPhoneNumber(row.otherNumber)}
                        </strong>
                        <small className='text-muted'>{moment(row.creationDate).format('MM/DD')}</small>
                      </div>
                      <small className='mini-messenger__convo-preview'>
                        {row.direction === 'Outbound' ? 'You: ' : ''}
                        {row.text}
                      </small>
                    </div>
                  )
                )}
            </>
          ) : (
            <>
              {visibleConversations.length === 0 && (
                <p className='text-muted text-center mt-4'>
                  {onlyUnreadConvos ? 'No contacts with new messages' : 'No conversations yet'}
                </p>
              )}
              {visibleConversations.map((convo) => (
                <div
                  key={convo.convoKey}
                  className={`mini-messenger__convo ${
                    selected && selected.convoKey === convo.convoKey ? 'mini-messenger__convo--active' : ''
                  } ${flashConvoKeys.has(convo.convoKey) ? 'mini-messenger__convo--new' : ''}`}
                  onClick={() => selectConversation(convo)}
                >
                  <div className='d-flex justify-content-between align-items-center'>
                    <strong className='mini-messenger__convo-name'>{conversationTitle(convo)}</strong>
                    <small className='text-muted'>{moment(convo.lastDate).format('MM/DD')}</small>
                  </div>
                  <div className='d-flex justify-content-between align-items-center'>
                    <small className='mini-messenger__convo-preview'>
                      {convo.lastDirection === 'Outbound' ? 'You: ' : ''}
                      {convo.lastText}
                    </small>
                    {convo.unreadCount > 0 && (
                      <Badge color='danger' pill>
                        {convo.unreadCount}
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      <div className='mini-messenger__chat'>
        {!selected ? (
          <p className='text-muted text-center mt-5'>Select a conversation</p>
        ) : (
          <>
            <div className='mini-messenger__chat-header'>
              <strong>{conversationTitle(selected)}</strong>
              <div className='mini-messenger__header-actions'>
                <i
                  className={`fas fa-filter mini-messenger__filter ${
                    onlyUnreadThread ? 'mini-messenger__filter--active' : ''
                  }`}
                  title={onlyUnreadThread ? 'Showing only unread messages' : 'Show only unread messages'}
                  onClick={toggleOnlyUnreadThread}
                />
                {selected.contactId && (
                  // window.open bez rel=noopener (app konvencija, npr.
                  // CompanyInformation.jsx) — Chrome tada kopira sessionStorage
                  // (CRMToken) u novu tab; sa noopener bi nova tab bila bez tokena
                  // i redirektovala na login.
                  <a
                    href={`/contacts/${selected.contactId}`}
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(`/contacts/${selected.contactId}`, '_blank')
                    }}
                  >
                    Open contact <i className='fas fa-external-link-alt' />
                  </a>
                )}
              </div>
            </div>
            <div className='mini-messenger__thread'>
              {onlyUnreadThread && visibleThread.length === 0 && (
                <p className='text-muted text-center mt-4'>No unread messages</p>
              )}
              {visibleThread.map((sms) => {
                const day = dayLabel(sms.creationDate)
                const separator = day !== lastDay ? day : null
                lastDay = day
                const outgoing = sms.direction === 'Outbound'
                return (
                  <React.Fragment key={sms.id}>
                    {separator && <div className='mini-messenger__day'>{separator}</div>}
                    <div className={`mini-messenger__bubble-row ${outgoing ? 'mini-messenger__bubble-row--out' : ''}`}>
                      <div
                        className={`mini-messenger__bubble ${outgoing ? 'mini-messenger__bubble--out' : ''} ${
                          flashMessageIds.has(sms.id) ? 'mini-messenger__bubble--new' : ''
                        }`}
                      >
                        {sms.fromNumber && (
                          <div className='mini-messenger__bubble-from'>{formatPhoneNumber(sms.fromNumber)}</div>
                        )}
                        <div className='mini-messenger__bubble-text'>{sms.text}</div>
                        <div
                          className='mini-messenger__bubble-time'
                          title={moment(sms.creationDate).format('MMM DD, YYYY hh:mm A')}
                        >
                          {moment(sms.creationDate).fromNow()}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                )
              })}
              <div ref={threadEndRef} />
            </div>
            <div className='mini-messenger__compose'>
              <Input
                type='textarea'
                rows={1}
                placeholder='Send text message'
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onComposeKeyDown}
              />
              <Button
                color='primary'
                onClick={send}
                disabled={!text.trim() || text.length > MAX_SMS_LENGTH || sending}
                title='Send'
              >
                <i className='fas fa-paper-plane' />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default MiniMessenger
